/**
 * Дашборд плановых заказов — где застряли активные партии: категория, ступень
 * цепочки, фабрика. Числа кликабельны и уводят на «Планы» тем же отбором,
 * каким посчитаны (`@/lib/planDash`).
 *
 * Спека: `docs/specs/2026-09-23-plans-dashboard-design.md`.
 *
 * СТУПЕНИ ПРИЕЗЖАЮТ С СЕРВЕРА и уже в порядке жизни партии: пустых на экране
 * нет вовсе, а у категорий, кроме платьев, нет и ступени техкарты — её там не
 * спрашивают.
 */

import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker, созданныеОкна } from "@/components/DateRangePicker";
import { Segmented } from "@/components/Segmented";
import { apiFetch } from "@/lib/api";
import { isValidDate } from "@/lib/dates";
import { nf } from "@/lib/format";
import {
  type DashCategory,
  type DashFilter,
  dashHref,
  type DashPick,
  type DashState,
  type DashSupplier,
  pickedHrefs,
  pickKey,
  type PlanDashboard,
  recallCreated,
  rememberCreated,
  STATE_HINTS,
  STATE_LABELS,
  stateHref,
  suppliersOf,
  tilePicks,
  type Tri,
} from "@/lib/planDash";
import { plural } from "@/lib/plans";
import { useLoad } from "@/lib/useLoad";

/** Последний период «создан» дашборда — `rememberCreated`. */
const CREATED_KEY = "supply.plans.dash.created";
const ROWS = ["строка", "строки", "строк"] as const;

/** Два числа рядом: строк (партий-дат) и штук плана внутри них. */
function Числа({ plans, qty }: { plans: number; qty: number }) {
  return (
    <span className="tnum flex-none text-[11.5px] text-fg-faint">
      {nf.format(plans)} {plural(plans, ROWS)} · {nf.format(qty)} шт
    </span>
  );
}

/** Галочка — это фабрика в ступени категории; ключ один на весь экран. */
const ключ = (category: DashCategory, state: DashState | null, supplier: number) =>
  pickKey({ category, state: state?.state ?? null, supplier });

/** Галочка плитки: третье состояние — свойством узла, атрибута у него нет. */
function TileCheck({
  picks,
  picked,
  onChange,
}: {
  picks: DashPick[];
  picked: ReadonlyMap<string, DashPick>;
  onChange: (picks: DashPick[], on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const n = picks.filter((p) => picked.has(pickKey(p))).length;
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = n > 0 && n < picks.length;
  }, [n, picks.length]);
  if (!picks.length) return null;
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={n === picks.length}
      onChange={() => onChange(picks, n < picks.length)}
      aria-label="отметить все фабрики плитки"
      title="отметить все фабрики плитки — «Открыть» покажет их одной вкладкой"
      className="h-3.5 w-3.5 flex-none translate-y-0.5 cursor-pointer accent-[var(--color-accent)]"
    />
  );
}

/** Те же варианты, что у переключателей «Списка» (`PlansPage.tsx`). */
const ТРИ = [
  { value: "" as Tri, label: "все" },
  { value: "yes" as Tri, label: "есть" },
  { value: "no" as Tri, label: "нет" },
];
const ТРИ_ОПЛАТА = [
  { value: "" as Tri, label: "все" },
  { value: "yes" as Tri, label: "оплачено" },
  { value: "no" as Tri, label: "нет" },
];

/** Мусор в адресе считается «все» — как на «Списке» (`тристорона`). */
const три = (value: string | null): Tri => (value === "yes" || value === "no" ? value : "");

/** Фабрика строкой: галочка, имя-ссылка, числа. */
/** Скопировать имя фабрики в буфер (разметка 29.09.26 15:59 п.1): галочка на
 *  полторы секунды, при отказе буфера — красный значок. */
function CopyName({ text }: { text: string }) {
  const [state, setState] = useState<"idle" | "done" | "fail">("idle");
  function copy() {
    if (!navigator.clipboard) {
      setState("fail");
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => {
        setState("done");
        window.setTimeout(() => setState((was) => (was === "done" ? "idle" : was)), 1500);
      },
      () => setState("fail"),
    );
  }
  const Icon = state === "done" ? Check : Copy;
  return (
    <button
      type="button"
      onClick={copy}
      title={state === "fail" ? "не вышло — ещё раз" : `скопировать «${text}»`}
      aria-label={`скопировать ${text}`}
      data-annot="копировать имя фабрики"
      className={
        "ml-1 flex-none self-center rounded-[2px] p-0.5 hover:bg-bg-subtle hover:text-fg" +
        (state === "fail" ? " text-err" : state === "done" ? " text-ok" : " text-fg-faint")
      }
    >
      <Icon size={12} aria-hidden />
    </button>
  );
}

function Фабрика({
  category,
  state,
  supplier,
  filter,
  picked,
  toggle,
}: {
  category: DashCategory;
  state: DashState | null;
  supplier: DashSupplier;
  filter: DashFilter;
  picked: ReadonlyMap<string, DashPick>;
  toggle: (key: string, pick: DashPick) => void;
}) {
  const href = stateHref(state?.state ?? null, category, supplier.id, filter);
  const title = state
    ? `${STATE_LABELS[state.state]} — открыть эти строки на «Планах» в новой вкладке`
    : "все активные строки фабрики в категории — открыть на «Планах» в новой вкладке";
  // Партии без поставщика галочки нет: номера у них нет, и в адрес их не
  // положить.
  return (
    <li className="flex items-baseline gap-2 pl-1 text-[12.5px] text-fg-muted">
      {supplier.id === null ? (
        <span className="w-3.5 flex-none" />
      ) : (
        <input
          type="checkbox"
          checked={picked.has(ключ(category, state, supplier.id))}
          onChange={() =>
            toggle(ключ(category, state, supplier.id as number), {
              category,
              state: state?.state ?? null,
              supplier: supplier.id as number,
            })
          }
          aria-label={`отметить ${supplier.name}`}
          className="h-3.5 w-3.5 flex-none translate-y-0.5 cursor-pointer accent-[var(--color-accent)]"
        />
      )}
      {/* Вся строка — ссылка в НОВУЮ вкладку (разметка 29.09.26): дашборд
          остаётся открытым, как и у «Открыть». Кнопка копирования имени стоит
          МЕЖДУ двумя половинами ссылки: кнопке внутри <a> не место. */}
      <Link
        to={href}
        target="_blank"
        rel="noopener"
        title={title}
        className="min-w-0 truncate hover:text-accent-text"
      >
        {supplier.name}
      </Link>
      <CopyName text={supplier.name} />
      <Link
        to={href}
        target="_blank"
        rel="noopener"
        title={title}
        tabIndex={-1}
        className="flex flex-1 justify-end hover:text-accent-text"
      >
        <Числа plans={supplier.plans} qty={supplier.qty} />
      </Link>
    </li>
  );
}

function Ступень({
  category,
  state,
  filter,
  picked,
  toggle,
}: {
  category: DashCategory;
  state: DashState;
  filter: DashFilter;
  picked: ReadonlyMap<string, DashPick>;
  toggle: (key: string, pick: DashPick) => void;
}) {
  return (
    <div className="border-t border-border pt-1.5 pb-2" data-annot={STATE_LABELS[state.state]}>
      <div className="flex items-baseline gap-2">
        <Link
          to={dashHref(state.state, category, filter)}
          title={STATE_HINTS[state.state]}
          className="text-[12.5px] font-medium text-fg underline decoration-border underline-offset-2 hover:decoration-fg"
        >
          {STATE_LABELS[state.state]}
        </Link>
        <span className="ml-auto">
          <Числа plans={state.plans} qty={state.qty} />
        </span>
      </div>
      <ul className="mt-1 flex flex-col gap-0.5">
        {state.suppliers.map((supplier) => (
          <Фабрика
            key={supplier.id ?? 0}
            category={category}
            state={state}
            supplier={supplier}
            filter={filter}
            picked={picked}
            toggle={toggle}
          />
        ))}
      </ul>
    </div>
  );
}

export function PlanDashboardPage() {
  const [data, setData] = useState<PlanDashboard | null>(null);
  const [failure, setFailure] = useState("");
  const [params, setParams] = useSearchParams();
  // Галочки всего экрана под ОДНОЙ кнопкой «Открыть» (разметка 28.09.26):
  // своя кнопка у каждой ступени плодила их по экрану. Map держит порядок
  // отметок — в нём же откроются вкладки.
  const [picked, setPicked] = useState<ReadonlyMap<string, DashPick>>(new Map());
  const toggle = (key: string, pick: DashPick) =>
    setPicked((now) => {
      const next = new Map(now);
      if (next.has(key)) next.delete(key);
      else next.set(key, pick);
      return next;
    });
  // Плитка целиком (просьба владельца 23.10.26): частичная отмечается вся,
  // полная снимается.
  const setMany = (picks: DashPick[], on: boolean) =>
    setPicked((now) => {
      const next = new Map(now);
      for (const p of picks) {
        if (on) next.set(pickKey(p), p);
        else next.delete(pickKey(p));
      }
      return next;
    });

  // Окно по дате создания живёт в адресе под теми же ключами, что у «Планов»:
  // ссылку на дашборд с отбором можно переслать, а клик по плитке уносит его
  // дальше без перевода. Мусорная дата из адреса считается незаданной — как в
  // самом календаре, — иначе ручка ответила бы 422 вместо экрана.
  const cfrom = params.get("cfrom") ?? "";
  const cto = params.get("cto") ?? "";
  // Отборы QR-папки, оплаты и техкарты (разметка 28.09.26) — те же ключи
  // адреса, что на «Списке», и клик плитки уносит их туда без перевода.
  const filter: DashFilter = {
    from: isValidDate(cfrom) ? cfrom : "",
    to: isValidDate(cto) ? cto : "",
    qrhas: три(params.get("qrhas")),
    paid: три(params.get("paid")),
    card: три(params.get("card")),
  };
  // По умолчанию — просто фабрики категории (разметка 29.09.26); ступени —
  // `group=states`. Старый `group=off` по-прежнему значит «без».
  const поСтадиям = params.get("group") === "states";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  // Заход без периода в адресе получает последний выбранный (разметка
  // 29.09.26 07:30). Период в адресе главнее: пересланная ссылка открывается
  // своим отбором, а не чужой памятью.
  // Период из памяти встаёт в адрес СЛЕДУЮЩИМ рендером, а запрос этого рендера
  // ушёл бы без периода — второй, лишний `/api/plans/dashboard`. Флаг просит
  // эффект загрузки ниже пропустить один заход (аудит 12.10.26, B46).
  const ждёмПамять = useRef(false);
  useEffect(() => {
    if (params.has("cfrom") || params.has("cto")) return;
    let память: unknown;
    try {
      память = JSON.parse(localStorage.getItem(CREATED_KEY) ?? "null");
    } catch {
      return;
    }
    const было = recallCreated(память, созданныеОкна(), isValidDate);
    if (было) {
      ждёмПамять.current = true;
      setCreated(было.from, было.to, false);
    }
    // Только на заходе: дальше период правит сам человек.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCreated = (from: string, to: string, remember = true) => {
    if (remember)
      try {
        localStorage.setItem(CREATED_KEY, JSON.stringify(rememberCreated(from, to, созданныеОкна())));
      } catch {
        // Хранилище закрыто — период просто не запомнится.
      }
    const next = new URLSearchParams(params);
    if (from) next.set("cfrom", from);
    else next.delete("cfrom");
    if (to) next.set("cto", to);
    else next.delete("cto");
    setParams(next, { replace: true });
  };

  // Всегда в новой вкладке — дашборд остаётся открытым со своими отметками.
  const open = () => {
    for (const href of pickedHrefs([...picked.values()], filter))
      window.open(href, "_blank", "noopener");
  };

  useLoad(
    () => {
      if (ждёмПамять.current) {
        ждёмПамять.current = false;
        return undefined;
      }
      const query = new URLSearchParams();
      if (filter.from) query.set("created_from", filter.from);
      if (filter.to) query.set("created_to", filter.to);
      // `yes`/`no` адреса — `true`/`false` ручки.
      const api = { qrhas: "has_qr", paid: "is_paid", card: "has_card" } as const;
      for (const key of ["qrhas", "paid", "card"] as const)
        if (filter[key]) query.set(api[key], filter[key] === "yes" ? "true" : "false");
      const tail = query.size ? `?${query}` : "";
      setData(null);
      setFailure("");
      // Новый отбор — новые плитки: старые галочки могли остаться на
      // фабриках, которых в плитке больше нет, и открыли бы пустой отбор.
      setPicked(new Map());
      return apiFetch<PlanDashboard>(`/api/plans/dashboard${tail}`);
    },
    [filter.from, filter.to, filter.qrhas, filter.paid, filter.card],
    { done: setData, fail: (error) => setFailure(error.message) },
  );

  // Смена группировки меняет и сами галочки: отметка «фабрика в ступени» без
  // ступеней не видна, а открыла бы свою вкладку.
  useEffect(() => setPicked(new Map()), [поСтадиям]);

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Пояснение над отбором снято (разметка 28.09.26): календарь — слева. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/* Дата СОЗДАНИЯ партии (разметка владельца 25.09.26) — тот же отбор,
            что «создан» на «Планах», с теми же окнами назад. */}
        <DateRangePicker
          label="создан"
          from={filter.from}
          to={filter.to}
          windows={созданныеОкна()}
          onChange={setCreated}
        />
        <Segmented
          label="стадии"
          title="по стадиям — фабрики внутри ступеней воронки; без — просто фабрики категории"
          options={[
            { value: "states", label: "по стадиям" },
            { value: "", label: "без" },
          ]}
          isOn={(v) => (v === "states") === поСтадиям}
          onPick={(v) => setParam("group", v)}
          annot="группировка по стадиям"
        />
        <Segmented
          label="QR"
          title="партии в QR-папке либо без неё"
          options={ТРИ}
          isOn={(v) => filter.qrhas === v}
          onPick={(v) => setParam("qrhas", v)}
          annot="отбор QR-папки"
        />
        <Segmented
          label="оплата"
          title="оплачена ли QR-папка партии; партии без папки считаются неоплаченными"
          options={ТРИ_ОПЛАТА}
          isOn={(v) => filter.paid === v}
          onPick={(v) => setParam("paid", v)}
          annot="отбор оплаты"
        />
        <Segmented
          label="карта"
          title="техкарта партии в CRM"
          options={ТРИ}
          isOn={(v) => filter.card === v}
          onPick={(v) => setParam("card", v)}
          annot="отбор техкарты"
        />
        {picked.size ? (
          <>
            <button
              type="button"
              onClick={open}
              title="Открыть на «Планах» строки отмеченных поставщиков — в новой вкладке; отметки из разных плиток — разными вкладками"
              className="rounded border border-accent bg-accent-dim px-2 py-0.5 text-[12px] text-fg hover:border-fg-faint"
            >
              Открыть · {picked.size}
            </button>
            <button
              type="button"
              onClick={() => setPicked(new Map())}
              className="text-[12px] text-fg-muted hover:text-fg"
            >
              снять отметки
            </button>
          </>
        ) : null}
      </div>
      {data && !data.categories.length ? (
        <div className="text-[12px] text-fg-muted">
          Активных партий под этим отбором нет.
        </div>
      ) : null}

      {failure ? <div className="text-[12px] text-err">Не приехало: {failure}</div> : null}
      {!data && !failure ? <div className="text-[12px] text-fg-muted">считаю…</div> : null}

      {data ? (
        <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          {data.categories.map((category) => (
            <section
              key={category.id}
              className="rounded border border-border bg-bg-subtle px-3 pt-2 pb-1"
              data-annot={`категория ${category.name}`}
            >
              <header className="flex items-baseline gap-2 pb-1.5">
                <TileCheck picks={tilePicks(category, поСтадиям)} picked={picked} onChange={setMany} />
                <h2 className="truncate text-[13px] font-medium text-fg">{category.name}</h2>
                <span className="ml-auto">
                  <Числа plans={category.plans} qty={category.qty} />
                </span>
              </header>
              {поСтадиям ? (
                category.states.map((state) => (
                  <Ступень
                    key={state.state}
                    category={category}
                    state={state}
                    filter={filter}
                    picked={picked}
                    toggle={toggle}
                  />
                ))
              ) : (
                <ul className="flex flex-col gap-0.5 border-t border-border pt-1.5 pb-2">
                  {suppliersOf(category).map((supplier) => (
                    <Фабрика
                      key={supplier.id ?? 0}
                      category={category}
                      state={null}
                      supplier={supplier}
                      filter={filter}
                      picked={picked}
                      toggle={toggle}
                    />
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}
